import { restartableTask } from 'ember-concurrency';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import embedPdf from '../../../modifiers/embed-pdf.ts';
import Footer from 'tio-ui/components/footer';
import Modal from 'tio-ui/components/modal';
import type RecommendationModel from 'tio-data/models/recommendation';
import type StoreService from '@ember-data/store';
import { t } from 'ember-intl';
import type UserModel from 'tio-data/models/user';
import Table from 'tio-ui/components/table';
import safeFormatDate from '../../../helpers/safe-format-date.ts';
import { fn } from '@ember/helper';
import TioPreviewButton from '../../tio/preview-button.gts';

interface ProfileRecommendationsTableSignature {
  Args: {
    recommendationType: string;
    user: UserModel;
    limit?: number;
  };
}

export default class ProfileRecommendationsTableComponent extends Component<ProfileRecommendationsTableSignature> {
  @service declare store: StoreService;

  @tracked showPreview = false;
  @tracked pdfSrc = '';

  get headers() {
    return this.store.adapterFor('application')?.headers;
  }

  get apiHost() {
    return this.store.adapterFor('application')?.host;
  }

  showPreviewModal = async (recommendation: RecommendationModel) => {
    const res = await fetch(`${this.apiHost}/recommendations/${recommendation.id}.pdf`, {
      headers: this.headers,
    });
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    this.pdfSrc = url;
    this.showPreview = true;
  };

  closePreviewModal = () => {
    this.showPreview = false;
    this.pdfSrc = '';
  };

  get results(): RecommendationModel[] {
    return this.data.value || [];
  }

  get userId() {
    return this.args.user?.id;
  }

  get recommendationType() {
    return this.args.recommendationType;
  }

  data = trackedFunction(this, async () => {
    // @ts-expect-error: Task return type not properly inferred
    const results = await this.loadRecords.perform(this.userId, this.recommendationType);
    return results;
  });

  get isLoading() {
    return this.data.isLoading;
  }

  loadRecords = restartableTask(async (userId: string, recommendationType: string) => {
    if (!userId) return;

    const page = {
      limit: this.args.limit || 10,
      offset: 0,
    };

    const filter = {
      user: userId,
      'requesting-app': recommendationType,
    };

    const sort = '-created-at';

    const query = { filter, page, sort };
    return (await this.store.query<RecommendationModel>(
      'recommendation',
      query
    )) as RecommendationModel[];
  });

  <template>
    <Table class="p-4" @isLoading={{this.isLoading}}>
      <:thead as |options|>
        <options.tr>
          <options.th />
          <options.th>{{t "common.recommendations.date"}}</options.th>
          <options.th>{{t "common.recommendations.results"}}</options.th>
        </options.tr>
      </:thead>
      <:tbody as |options|>
        {{#each this.results as |result|}}
          <options.tr>
            <options.td>
              <TioPreviewButton @onClick={{fn this.showPreviewModal result}} />
            </options.td>
            {{#let
              (safeFormatDate result.createdAt month="long" day="2-digit" year="numeric")
              as |date|
            }}
              <options.td title={{date}}>{{date}}</options.td>
            {{/let}}
            <options.td>{{result.bestPlanTableRowSummary}}</options.td>
          </options.tr>
        {{else}}
          <options.tr>
            <options.td colspan="3">
              {{t "common.recommendations.no_results"}}
            </options.td>
          </options.tr>
        {{/each}}
      </:tbody>
    </Table>

    <Modal
      @isOpen={{this.showPreview}}
      @onClose={{this.closePreviewModal}}
      @ariaLabel="PDF Preview"
      class="min-h-64"
      @size="full"
    >
      <:body>
        {{#if this.pdfSrc}}
          <div {{embedPdf this.pdfSrc}}></div>
        {{/if}}
      </:body>
      <:footer>
        <Footer
          @isRunning={{false}}
          @onSubmit={{this.closePreviewModal}}
          @submitText="Close Preview"
        />
      </:footer>
    </Modal>
  </template>
}
